import { useEffect, useMemo, useRef, useState } from "react";

import { Box, IconButton, Skeleton, Stack, keyframes } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { PublicityBanner, IPublicityBannerProps } from "@gisce/oficina-virtual-components";
import useLangs from "@/languages/hooks/useLangs";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { fetchCoupons } from "@/actions/coupons";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import Banner from "../Banner/Banner";
import { fetchBannersCodes } from "@/actions/banners";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const bannerFitSx = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  "& img": {
    width: "100%",
    height: "auto",
    display: "block",
  },
  "& > div": {
    width: "100%",
  },
};

type CouponSlide = { kind: "coupon"; data: IPublicityBannerProps["dataProps"] };
type BannerSlide = { kind: "banner"; code: string };
type Slide = CouponSlide | BannerSlide;

export const CouponsView = () => {
  const dispatch = useAppDispatch();
  const { currentLang } = useLangs();
  const isAuthenticated = useAppSelector(store => store.auth.isAuthenticated);
  const coupons: IPublicityBannerProps["dataProps"][] = useAppSelector(state => state.coupons.data);
  const fetchingCoupons: boolean = useAppSelector(state => state.coupons.fetching);
  const Settings = useSettingsContext();
  const [currentIndex, setCurrentIndex] = useState(0);

  const bannerCodesVariable = 'publicity_banners_codes'
  const bannerCodes: string[] = useAppSelector(state => 
    bannerCodesVariable ? state.banners.bannersCodes[bannerCodesVariable] : []
  ) ?? [];

  const fetchingBannersCodes = useAppSelector(state => 
    bannerCodesVariable ? state.banners.fetching[bannerCodesVariable] : false
  );

  const [bannerHasContent, setBannerHasContent] = useState<Record<string, boolean>>({});

  const handleBannerContentChange = (code: string) => (hasContent: boolean) => {
    setBannerHasContent(prev => (prev[code] === hasContent ? prev : { ...prev, [code]: hasContent }));
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (Settings.features.showCoupons) {
        dispatch(fetchCoupons(currentLang));
      }

      if (bannerCodesVariable) {
        dispatch(fetchBannersCodes(bannerCodesVariable));
      }
    }
  }, [isAuthenticated, currentLang, bannerCodesVariable, dispatch]);

  const slides: Slide[] = useMemo(() => {
    const couponSlides: CouponSlide[] = Array.isArray(coupons)
      ? coupons.map(data => ({ kind: "coupon" as const, data }))
      : [];

    const bannerSlides: BannerSlide[] = bannerCodes
      .filter(code => bannerHasContent[code])
      .map(code => ({ kind: "banner" as const, code }));

    return [...couponSlides, ...bannerSlides];
  }, [coupons, bannerCodes, bannerHasContent]);

  const goToPrevious = () => {
    if (slides.length === 0) {
      return;
    }
    setCurrentIndex(prev => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToNext = () => {
    if (slides.length === 0) {
      return;
    }
    setCurrentIndex(prev => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const fetchingCouponsPending = !!Settings.features.showCoupons && fetchingCoupons;
  const isFetching = fetchingCouponsPending || fetchingBannersCodes;

  useEffect(() => {
    if (!isAuthenticated || isFetching || slides.length === 0) return;
    const interval = setInterval(
      goToNext,
      Settings?.features?.coupons?.carouselInterval ?? 10000
    );
    return () => clearInterval(interval);
  }, [slides.length, isFetching, isAuthenticated, Settings?.features?.coupons?.carouselInterval]);

  useEffect(() => {
    if (currentIndex >= slides.length) {
      setCurrentIndex(0);
    }
  }, [slides.length, currentIndex]);

  const renderSlide = (currentIndex: number, slide: Slide) => {
    if (slide.kind === "coupon") {
      return (
        <Box
          key={currentIndex}
          sx={{
            animation: `${fadeIn} 0.4s ease-out`,
            boxShadow: 3,
            borderRadius: 3,
            overflow: "hidden"
          }}
        >
          <PublicityBanner dataProps={slide.data} />
        </Box>
      )
    }
    return (
      <Box
        key={currentIndex}
        sx={{
          height: "100%",
          animation: `${fadeIn} 0.3s ease-out`,
          boxShadow: 3,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
          <Banner code={slide.code} sx={bannerFitSx} />
        </Box>
    )
  };

  const showSkeleton = isFetching && slides.length === 0;

  return (
    <Box sx={{ position: "relative", width: "100%"}}>
      {bannerCodes
        .filter(code => !bannerHasContent[code])
        .map(code => (
          <Box key={code} sx={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
            <Banner code={code} onContentChange={handleBannerContentChange(code)} />
          </Box>
        ))
      }

      {showSkeleton && (
        <Skeleton
          variant="rounded"
          sx={{ height: "100%", width: "100%", borderRadius: 3 }}
        />
      )}

      {!showSkeleton && slides.length === 1 && (
        renderSlide(0, slides[0])
      )}

      {!showSkeleton && slides.length > 1 && (
        <>
          {renderSlide(currentIndex, slides[currentIndex])}
          <IconButton
            onClick={goToPrevious}
            aria-label="Previous coupon"
            sx={{
              position: "absolute",
              left: 32,
              bottom: { xs: 40, md: 60 },
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={goToNext}
            aria-label="Next coupon"
            sx={{
              position: "absolute",
              right: 32,
              bottom: { xs: 40, md: 60 },
              transform: "translateY(0)",
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <ChevronRight />
          </IconButton>
          <Stack data-testid="carousel-dots" direction="row" spacing={1} justifyContent="center" sx={{ mt: 3, mb: 2 }}>
            {slides.map((_, idx) => (
              <Box
                key={idx}
                data-testid={`carousel-dot-${idx}`}
                onClick={() => setCurrentIndex(idx)}
                sx={{
                  width: idx === currentIndex ? 24 : 8,
                  height: 8,
                  borderRadius: 1,
                  cursor: "pointer",
                  backgroundColor: idx === currentIndex ? "primary.main" : "text.secondary",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};
